/**
 * 账务明细
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Table, Pagination } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SyncOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import { format2DateStr, format2FullTimeStr } from '@/utils/date';
import { parseFen2Currency } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import { PAYMENT_TYPE_MAP, BILL_STATE_MAP } from '@/config/constant';
import { createMapRender, DrawerContext, HandoverInfo } from '@/pages/handover/helper';
import {
  fetchHandoverBillDetailStatus,
  fetchHandoverBillDetailReport,
  refreshHandoverBillDetailReport,
  exportHandoverBillDetailReport,
} from '@/api/handover';
import FixStickyTable from '@/components/FixStickyTable';
import { downloadFile } from '@/utils';

import styles from './index.module.scss';

export const PostTypeMap = {
  0: intl.get('handover_detail_post_type_0'),
  1: intl.get('handover_detail_post_type_1'),
  2: intl.get('handover_detail_post_type_2'),
  3: intl.get('handover_detail_post_type_3'),
  4: intl.get('handover_detail_post_type_4'),
  11: intl.get('handover_detail_post_type_11'),
  41: intl.get('handover_detail_post_type_41'),
};

interface IRecord {
  amount: number; // 付款
  billItemNo: string; // 账务单号
  businessDate: number; // 营业日
  businessExplain: string; //	财务项目
  channelNo: string; //	渠道单号
  commonCode: string; // 门店码
  consumption: number; // 消费
  createName: string; // 操作人
  detail: string; //	财务详情
  masterName: string; // 房主预定人
  orderNo: string; //	流水号
  paymentType: string; // 付款渠道
  postTime: number; // 账务时间
  postType: number; // 入账类型
  remark: string; // 备注
  resvNo: string; // 主订单号
  roomNo: string; // 房号
  settlementNo: string; // 结账单号
  shiftId: number; // 交班ID
  status: number; // 结账状态
  taskId: number; // 任务ID
}

enum TaskStatus {
  Todo = 1,
  Pending = 2,
  Done = 3,
}

interface TaskData {
  status: TaskStatus; // 1-未开始，2-已开始，3-已完成
  updateTime: number; // 更新时间
}
interface IProps {
  handoverInfo: HandoverInfo;
}

const defaultPageSize = 50;

const Index: React.FC<IProps> = (props) => {
  const [pageData, setPageData] = useState({ current: 1, total: 0 });
  const [listData, setListData] = useState<IRecord[]>([]);
  const [taskData, setTaskData] = useState<TaskData>(null);
  const { setLoadingStatus, handoverId } = useContext(DrawerContext);
  const { handoverInfo } = props;
  const taskStatus = taskData?.status || TaskStatus.Todo;

  // 查询列表
  const getTableData = async (query) => {
    setLoadingStatus(true);
    try {
      const params = {
        pageNum: 1,
        pageSize: defaultPageSize,
        ...query,
        id: handoverId,
      };
      const res = await fetchHandoverBillDetailReport(params);
      const { list, pageNum, total } = res.data;
      setListData(
        (list || []).map((item) => {
          return {
            ...item,
            indexId: getRandomId(),
          };
        }),
      );
      setPageData({ current: pageNum, total });
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  // 轮询状态
  const { startQueryTask, stopQueryTask } = useMemo(() => {
    let isTaskCancel = false;
    return {
      startQueryTask: async () => {
        try {
          const res = await fetchHandoverBillDetailStatus({ shiftId: handoverId });
          if (isTaskCancel) return;
          if (res.data && res.data.status === TaskStatus.Done) {
            getTableData({ pageNum: 1 });
            setTaskData({ status: TaskStatus.Done, updateTime: res.data.uptime });
          } else {
            throw new Error('LOOP');
          }
        } catch (e) {
          //
          setTimeout(() => {
            if (isTaskCancel) return;
            startQueryTask();
          }, 2 * 1000);
        }
      },
      stopQueryTask: () => {
        isTaskCancel = true;
      },
    };
  }, [handoverId]);

  // 刷新
  const refreshReportData = async () => {
    setLoadingStatus(true);
    try {
      await refreshHandoverBillDetailReport({ id: handoverId });
      setTaskData({ status: TaskStatus.Pending, updateTime: 0 });
      startQueryTask();
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  const initViewData = async () => {
    setLoadingStatus(true);
    try {
      const res = await fetchHandoverBillDetailStatus({ shiftId: handoverId });
      setLoadingStatus(false);
      const { status, updTime } = res.data || { status: TaskStatus.Todo }; // status: 1-未开始，2-已开始，3-已完成
      setTaskData({ status, updateTime: updTime });
      if (status === TaskStatus.Todo) {
        refreshReportData();
      }
      if (status === TaskStatus.Pending) {
        startQueryTask();
      }
      if (status === TaskStatus.Done) {
        getTableData({ pageNum: 1 });
      }
    } catch (e) {
      //
      setLoadingStatus(false);
    }
  };

  // 导出
  const handleExport = async () => {
    setLoadingStatus(true);
    try {
      const res = await exportHandoverBillDetailReport({ id: handoverId });
      downloadFile(
        res,
        intl.get('handover_detail_download_name', {
          name: handoverInfo.employeeName,
          time: format2FullTimeStr(handoverInfo.startTime),
        }),
      );
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  const renderCurrencyVal = (val) => {
    return parseFen2Currency(val);
  };

  const columns: ColumnsType<IRecord> = [
    { title: intl.get('handover_detail_column_resv'), dataIndex: 'resvNo' },
    { title: intl.get('handover_detail_column_order'), dataIndex: 'orderNo' },
    { title: intl.get('handover_detail_column_settle'), dataIndex: 'settlementNo' },
    { title: intl.get('handover_detail_column_name'), dataIndex: 'masterName' },
    { title: intl.get('handover_detail_column_room'), dataIndex: 'roomNo' },
    {
      title: intl.get('handover_detail_column_post'),
      dataIndex: 'postType',
      render: createMapRender(PostTypeMap),
    },
    { title: intl.get('handover_detail_column_project'), dataIndex: 'businessExplain' },
    {
      title: intl.get('handover_detail_column_payment'),
      dataIndex: 'paymentType',
      render: createMapRender(PAYMENT_TYPE_MAP),
    },
    { title: intl.get('handover_detail_column_channel'), dataIndex: 'channelNo' },
    { title: intl.get('handover_detail_column_detail'), dataIndex: 'detail', width: 200 },
    {
      title: intl.get('handover_detail_column_amount'),
      dataIndex: 'amount',
      render: renderCurrencyVal,
    },
    {
      title: intl.get('handover_detail_column_consume'),
      dataIndex: 'consumption',
      render: renderCurrencyVal,
    },
    {
      title: intl.get('handover_detail_column_date'),
      dataIndex: 'businessDate',
      render: format2DateStr,
    },
    {
      title: intl.get('handover_detail_column_time'),
      dataIndex: 'postTime',
      render: format2FullTimeStr,
    },
    {
      title: intl.get('handover_detail_column_status'),
      dataIndex: 'status',
      render: createMapRender(BILL_STATE_MAP),
    },
    { title: intl.get('handover_detail_column_creator'), dataIndex: 'createName' },
    { title: intl.get('handover_detail_column_remark'), dataIndex: 'remark', width: 200 },
  ];

  useEffect(() => {
    initViewData();
    return () => {
      stopQueryTask();
    };
  }, []);

  // 分页
  const handlePaginationChange = (pageNum) => {
    getTableData({ pageNum });
  };

  const renderTableContent = () => {
    if (taskStatus !== TaskStatus.Done) {
      return (
        <div>
          <div className="groupLabel">{intl.get('handover_detail_block_title')}</div>
          <div className={styles.loadingContent}>
            <SyncOutlined spin className={styles.syncIcon} />
            <div>{intl.get('handover_detail_loading_p1')}</div>
            <div>{intl.get('handover_detail_loading_p2')}</div>
            <div>{intl.get('handover_detail_loading_p3')}</div>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="groupLabel">{intl.get('handover_detail_block_title')}</div>
        <div className={styles.billTaskAction}>
          <div>
            <Button type="primary" onClick={refreshReportData}>
              {intl.get('handover_detail_refresh_btn')}
            </Button>
            <span className={styles.updateTimeText}>
              {intl.get('handover_detail_update_time', {
                time: format2FullTimeStr(taskData.updateTime),
              })}
            </span>
          </div>
          <Button type="primary" onClick={handleExport}>
            {intl.get('app_search_form_export_btn')}
          </Button>
        </div>
        <FixStickyTable>
          <Table
            rowKey="indexId"
            scroll={{ x: 'max-content' }}
            sticky
            dataSource={listData}
            columns={columns}
            pagination={false}
          />
        </FixStickyTable>
        <Pagination
          showQuickJumper
          hideOnSinglePage
          style={{ marginTop: '24px' }}
          showSizeChanger={false}
          current={pageData.current}
          pageSize={defaultPageSize}
          onChange={handlePaginationChange}
          showTotal={(total) =>
            intl.get('app_table_pagination_total', { total, size: defaultPageSize })
          }
          total={pageData.total}
        />
      </div>
    );
  };

  return <div className={styles.billDetailContainer}>{renderTableContent()}</div>;
};

export default Index;
